import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@halo/web-lib';
import {
  Store,
  Users,
  Cable,
  Building2,
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  ChevronRight,
  Zap,
  Pause,
  Database,
  TrendingUp,
  AlertTriangle,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { InfoBadge } from '../components/ui/Tooltip';

import shopifyLogo from '../assets/platforms/shopify.svg';
import metaLogo from '../assets/platforms/meta.svg';
import googleAdsLogo from '../assets/platforms/google-ads.svg';
import tiktokLogo from '../assets/platforms/tiktok.svg';
import tiktokShopLogo from '../assets/platforms/tiktok-shop.svg';
import pinterestLogo from '../assets/platforms/pinterest.svg';
import klaviyoLogo from '../assets/platforms/klaviyo.svg';
import amazonAdsLogo from '../assets/platforms/amazon-ads.svg';
import amazonSellerLogo from '../assets/platforms/amazon-seller.svg';
import ga4Logo from '../assets/platforms/ga4.svg';

// ─── Types ──────────────────────────────────────

interface AdminStats {
  userCount: number;
  storeCount: number;
  connectionCount: number;
  agencyClientCount: number;
}

interface IngestionConfig {
  id: string;
  storeId: number;
  platform: string;
  status: string;
  lastScheduledAt: string | null;
  lastBackfillAt: string | null;
  lastStoreActiveAt: string | null;
}

interface IngestionRun {
  id: string;
  storeId: number;
  platform: string;
  runType: string;
  triggerSource: string;
  status: string;
  startedAt: string;
  completedAt: string | null;
  rowsIngested: number;
  errorMessage: string | null;
}

interface QueueOverview {
  name: string;
  counts: { waiting: number; active: number; completed: number; failed: number; delayed: number };
}

interface IntegrityCheck {
  checkedAt: string;
  table: string;
  physicalRows: number;
  logicalRows: number;
  duplicateRows: number;
  healthy: boolean;
  storeBreakdown: Array<{ store_id: number; duplicates: number }>;
}

interface IntegrityReport {
  healthy: boolean;
  totalDuplicates: number;
  lastChecked: string | null;
  checks: IntegrityCheck[];
}

// ─── Constants ──────────────────────────────────

const PLATFORM_ICONS: Record<string, string> = {
  shopify: shopifyLogo, meta: metaLogo, google_ads: googleAdsLogo,
  tiktok_business: tiktokLogo, tiktok_shop: tiktokShopLogo, pinterest: pinterestLogo,
  klaviyo: klaviyoLogo, amazon_ads: amazonAdsLogo, amazon_seller: amazonSellerLogo, ga4: ga4Logo,
};

const PLATFORM_LABELS: Record<string, string> = {
  shopify: 'Shopify', meta: 'Meta Ads', google_ads: 'Google Ads',
  tiktok_business: 'TikTok Business', tiktok_shop: 'TikTok Shop', pinterest: 'Pinterest',
  klaviyo: 'Klaviyo', amazon_ads: 'Amazon Ads', amazon_seller: 'Amazon Seller', ga4: 'GA4',
};

const RUN_STATUS_STYLES: Record<string, { icon: React.ElementType; color: string }> = {
  completed: { icon: CheckCircle2, color: 'text-green-600 dark:text-green-400' },
  errored: { icon: AlertCircle, color: 'text-red-500' },
  running: { icon: Activity, color: 'text-blue-600 dark:text-blue-400' },
  awaiting_reports: { icon: Clock, color: 'text-amber-600' },
};

// ─── Helpers ────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function isStale(cfg: IngestionConfig): boolean {
  if (cfg.status !== 'active') return false;
  const last = cfg.lastStoreActiveAt ?? cfg.lastScheduledAt ?? cfg.lastBackfillAt;
  if (!last) return true;
  return Date.now() - new Date(last).getTime() > 24 * 60 * 60 * 1000;
}

// ─── Main Component ─────────────────────────────

export function DashboardPage() {
  const navigate = useNavigate();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: async ({ signal }) => {
      const res = await api.get<AdminStats>('/api/v1/admin/stats', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed');
      return res.data;
    },
  });

  const { data: configs } = useQuery({
    queryKey: ['admin', 'ingestion', 'configs'],
    queryFn: async ({ signal }) => {
      const res = await api.get<IngestionConfig[]>('/api/v1/ingestion/admin/configs', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed');
      return res.data;
    },
    refetchInterval: 30_000,
  });

  const { data: recentRuns } = useQuery({
    queryKey: ['admin', 'ingestion', 'recent-runs'],
    queryFn: async ({ signal }) => {
      const res = await api.get<IngestionRun[]>('/api/v1/ingestion/admin/runs?limit=10', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed');
      return res.data;
    },
    refetchInterval: 15_000,
  });

  const { data: queuesRaw } = useQuery({
    queryKey: ['admin', 'jobs'],
    queryFn: async ({ signal }) => {
      const res = await api.get<{ queues: QueueOverview[] }>('/api/v1/ingestion/admin/jobs', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed');
      return res.data.queues;
    },
    refetchInterval: 30_000,
  });

  const { data: integrity } = useQuery({
    queryKey: ['admin', 'integrity'],
    queryFn: async ({ signal }) => {
      const res = await api.get<IntegrityReport>('/api/v1/ingestion/admin/integrity', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed');
      return res.data;
    },
    refetchInterval: 60_000,
  });

  const queryClient = useQueryClient();
  const [integrityRunning, setIntegrityRunning] = useState(false);
  const runIntegrityCheck = useMutation({
    mutationFn: async () => {
      setIntegrityRunning(true);
      const res = await api.post<{ healthy: boolean }>('/api/v1/ingestion/admin/integrity/run');
      if (!res.success) throw new Error(res.error ?? 'Failed');
      return res.data;
    },
    onSettled: () => {
      setIntegrityRunning(false);
      queryClient.invalidateQueries({ queryKey: ['admin', 'integrity'] });
    },
  });

  // ─── Derived data ─────────────────────────────

  const allConfigs = configs ?? [];
  const queues = queuesRaw ?? [];
  const runs = recentRuns ?? [];

  const ingestionSummary = useMemo(() => {
    const active = allConfigs.filter((c) => c.status === 'active').length;
    const backfilling = allConfigs.filter((c) => c.status === 'backfilling').length;
    const paused = allConfigs.filter((c) => c.status === 'paused').length;
    const disabled = allConfigs.filter((c) => c.status === 'disabled').length;
    const stale = allConfigs.filter(isStale).length;
    return { total: allConfigs.length, active, backfilling, paused, disabled, stale };
  }, [allConfigs]);

  const platformBreakdown = useMemo(() => {
    const map = new Map<string, { total: number; active: number; errored: number; stale: number }>();
    for (const c of allConfigs) {
      const entry = map.get(c.platform) ?? { total: 0, active: 0, errored: 0, stale: 0 };
      entry.total++;
      if (c.status === 'active') entry.active++;
      if (c.status === 'disabled') entry.errored++;
      if (isStale(c)) entry.stale++;
      map.set(c.platform, entry);
    }
    return [...map.entries()].sort((a, b) => b[1].total - a[1].total);
  }, [allConfigs]);

  const queueSummary = useMemo(() => ({
    active: queues.reduce((s, q) => s + q.counts.active, 0),
    waiting: queues.reduce((s, q) => s + q.counts.waiting, 0),
    failed: queues.reduce((s, q) => s + q.counts.failed, 0),
  }), [queues]);

  const recentErrors = useMemo(() => runs.filter((r) => r.status === 'errored').slice(0, 5), [runs]);

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-0.5">Dashboard</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Platform overview and system health</p>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400 dark:text-gray-500">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          Live — auto-refreshing
        </div>
      </div>

      {/* ─── Top Stats ──────────────────────────── */}
      {statsLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-3" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <TopStatCard icon={Users} label="Users" value={stats?.userCount ?? 0} onClick={() => navigate('/users')} />
          <TopStatCard icon={Store} label="Stores" value={stats?.storeCount ?? 0} onClick={() => navigate('/stores')} />
          <TopStatCard icon={Cable} label="Connections" value={stats?.connectionCount ?? 0} onClick={() => navigate('/stores')} />
          <TopStatCard icon={Building2} label="Agency Clients" value={stats?.agencyClientCount ?? 0} onClick={() => navigate('/agency')} />
        </div>
      )}

      {/* ─── Ingestion Health + Queue Status + Integrity ─────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Ingestion Health */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Ingestion Health</span>
            </div>
            <button onClick={() => navigate('/ingestion')} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1">
              View all <ChevronRight className="w-3 h-3" />
            </button>
          </div>

          {/* Status pills */}
          <div className="flex flex-wrap gap-2 mb-4">
            <InfoBadge tip="Configs running normally with scheduled daily pulls.">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-900/50">
                <CheckCircle2 className="w-3.5 h-3.5" /> {ingestionSummary.active} Active
              </span>
            </InfoBadge>
            {ingestionSummary.backfilling > 0 && (
              <InfoBadge tip="Historical data backfill in progress. Daily pulls paused until complete.">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-900/50">
                  <RefreshCw className="w-3.5 h-3.5" /> {ingestionSummary.backfilling} Backfilling
                </span>
              </InfoBadge>
            )}
            {ingestionSummary.paused > 0 && (
              <InfoBadge tip="Manually paused configs. No data is being pulled.">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-900/50">
                  <Pause className="w-3.5 h-3.5" /> {ingestionSummary.paused} Paused
                </span>
              </InfoBadge>
            )}
            {ingestionSummary.stale > 0 && (
              <InfoBadge tip="Active configs that haven't pulled data in over 24 hours. May indicate connection or scheduling issues.">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-50 dark:bg-amber-950/40 text-amber-600 border border-amber-200 dark:border-amber-900/50">
                  <AlertTriangle className="w-3.5 h-3.5" /> {ingestionSummary.stale} Stale
                </span>
              </InfoBadge>
            )}
            {ingestionSummary.disabled > 0 && (
              <InfoBadge tip="Disabled configs, usually due to lost OAuth connections.">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/50">
                  <AlertCircle className="w-3.5 h-3.5" /> {ingestionSummary.disabled} Disabled
                </span>
              </InfoBadge>
            )}
          </div>

          {/* Platform breakdown */}
          <div className="space-y-1.5">
            {platformBreakdown.map(([platform, counts]) => {
              const icon = PLATFORM_ICONS[platform];
              const label = PLATFORM_LABELS[platform] ?? platform;
              const healthPct = counts.total > 0 ? Math.round((counts.active / counts.total) * 100) : 0;
              return (
                <div key={platform} className="flex items-center gap-3 py-1.5">
                  <div className="w-6 h-6 rounded flex items-center justify-center shrink-0">
                    {icon ? <img src={icon} alt={label} className="w-4 h-4" /> : <Database className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />}
                  </div>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-200 w-28 truncate">{label}</span>
                  <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${healthPct === 100 ? 'bg-green-500' : healthPct > 0 ? 'bg-amber-500' : 'bg-gray-300'}`}
                      style={{ width: `${Math.max(healthPct, 3)}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] w-24 justify-end">
                    <span className="text-green-600 dark:text-green-400">{counts.active} ok</span>
                    {counts.errored > 0 && <span className="text-red-500">{counts.errored} err</span>}
                    {counts.stale > 0 && <span className="text-amber-600">{counts.stale} stale</span>}
                  </div>
                </div>
              );
            })}
            {platformBreakdown.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 py-4 text-center">No ingestion configs yet</p>
            )}
          </div>
        </div>

        {/* Queue Status */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Job Queues</span>
            </div>
            <button onClick={() => navigate('/jobs')} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1">
              Details <ChevronRight className="w-3 h-3" />
            </button>
          </div>

          {/* Queue summary */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="text-center py-2 rounded-lg bg-blue-50 dark:bg-blue-950/40">
              <div className="text-lg font-bold text-blue-700 dark:text-blue-400">{queueSummary.active}</div>
              <div className="text-[10px] text-blue-500">Active</div>
            </div>
            <div className="text-center py-2 rounded-lg bg-amber-50 dark:bg-amber-950/40">
              <div className="text-lg font-bold text-amber-700 dark:text-amber-300">{queueSummary.waiting}</div>
              <div className="text-[10px] text-amber-500">Waiting</div>
            </div>
            <div className={`text-center py-2 rounded-lg ${queueSummary.failed > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <div className={`text-lg font-bold ${queueSummary.failed > 0 ? 'text-red-700' : 'text-gray-400'}`}>{queueSummary.failed}</div>
              <div className={`text-[10px] ${queueSummary.failed > 0 ? 'text-red-500' : 'text-gray-400'}`}>Failed</div>
            </div>
          </div>

          {/* Per-queue breakdown */}
          <div className="space-y-1.5">
            {queues.map((q) => {
              const total = q.counts.active + q.counts.waiting + q.counts.failed;
              const isHealthy = q.counts.failed === 0;
              return (
                <div key={q.name} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      q.counts.failed > 0 ? 'bg-red-500' :
                      q.counts.active > 0 ? 'bg-blue-500 animate-pulse' :
                      q.counts.waiting > 0 ? 'bg-amber-500' : 'bg-green-500'
                    }`} />
                    <span className="text-xs text-gray-700 dark:text-gray-200">{q.name}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {q.counts.active > 0 && <span className="text-[10px] text-blue-600 dark:text-blue-400">{q.counts.active}a</span>}
                    {q.counts.waiting > 0 && <span className="text-[10px] text-amber-600">{q.counts.waiting}w</span>}
                    {q.counts.failed > 0 && <span className="text-[10px] text-red-500">{q.counts.failed}f</span>}
                    {total === 0 && <span className="text-[10px] text-gray-300">idle</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── Data Integrity ─────────────────────── */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Data Integrity</span>
            {integrity && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                integrity.healthy
                  ? 'bg-green-50 text-green-600'
                  : 'bg-red-50 text-red-600'
              }`}>
                {integrity.healthy ? 'Healthy' : `${integrity.totalDuplicates.toLocaleString()} duplicates`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {integrity?.lastChecked && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                Last check: {timeAgo(integrity.lastChecked)}
              </span>
            )}
            <button
              onClick={() => runIntegrityCheck.mutate()}
              disabled={integrityRunning}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${integrityRunning ? 'animate-spin' : ''}`} />
              {integrityRunning ? 'Running...' : 'Run now'}
            </button>
          </div>
        </div>

        {!integrity ? (
          <p className="text-xs text-gray-400 dark:text-gray-500 py-4 text-center">No integrity checks yet — click &quot;Run now&quot; to start</p>
        ) : integrity.healthy ? (
          <div className="py-3 text-center">
            <CheckCircle2 className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <p className="text-xs text-gray-500 dark:text-gray-400">All tables clean — no duplicate rows detected</p>
          </div>
        ) : (
          <div className="space-y-2">
            {integrity.checks
              .filter((c) => !c.healthy)
              .map((c) => (
                <div key={c.table} className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-red-800 dark:text-red-400">{c.table}</span>
                    <span className="text-[10px] text-red-600 dark:text-red-400">
                      {c.duplicateRows.toLocaleString()} duplicate rows
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-red-500 mb-1.5">
                    <span>Physical: {c.physicalRows.toLocaleString()}</span>
                    <span>Logical: {c.logicalRows.toLocaleString()}</span>
                  </div>
                  {c.storeBreakdown.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {c.storeBreakdown.map((s) => (
                        <span key={s.store_id} className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400">
                          Store #{s.store_id}: {s.duplicates.toLocaleString()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            {integrity.checks.filter((c) => c.healthy).length > 0 && (
              <p className="text-[10px] text-gray-400 dark:text-gray-500">
                {integrity.checks.filter((c) => c.healthy).length} table(s) clean
              </p>
            )}
          </div>
        )}
      </div>

      {/* ─── Recent Activity + Errors ────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Runs */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Recent Activity</span>
            </div>
            <button onClick={() => navigate('/ingestion')} className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1">
              View all <ChevronRight className="w-3 h-3" />
            </button>
          </div>

          {runs.length === 0 ? (
            <p className="text-xs text-gray-400 dark:text-gray-500 py-6 text-center">No recent runs</p>
          ) : (
            <div className="space-y-1 max-h-[300px] overflow-auto">
              {runs.slice(0, 8).map((run) => {
                const rs = RUN_STATUS_STYLES[run.status] ?? RUN_STATUS_STYLES['completed']!;
                const RunIcon = rs.icon;
                const icon = PLATFORM_ICONS[run.platform];
                return (
                  <div key={run.id} className="flex items-center gap-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-950 rounded px-1 -mx-1 transition-colors">
                    <RunIcon className={`w-3.5 h-3.5 shrink-0 ${rs.color}`} />
                    {icon && <img src={icon} alt="" className="w-3.5 h-3.5 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-700 dark:text-gray-200 truncate">
                        Store #{run.storeId} &middot; {PLATFORM_LABELS[run.platform] ?? run.platform}
                      </div>
                      <div className="text-[10px] text-gray-400 dark:text-gray-500">
                        {run.rowsIngested > 0 && `${run.rowsIngested.toLocaleString()} rows · `}
                        {timeAgo(run.startedAt)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Errors */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Recent Errors</span>
              {recentErrors.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 font-medium">{recentErrors.length}</span>
              )}
            </div>
          </div>

          {recentErrors.length === 0 ? (
            <div className="py-6 text-center">
              <CheckCircle2 className="w-6 h-6 text-green-400 mx-auto mb-2" />
              <p className="text-xs text-gray-400 dark:text-gray-500">No recent errors</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[300px] overflow-auto">
              {recentErrors.map((run) => {
                const icon = PLATFORM_ICONS[run.platform];
                return (
                  <div key={run.id} className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg p-2.5">
                    <div className="flex items-center gap-2 mb-1">
                      {icon && <img src={icon} alt="" className="w-3.5 h-3.5" />}
                      <span className="text-[10px] font-medium text-red-800 dark:text-red-400">
                        Store #{run.storeId} &middot; {PLATFORM_LABELS[run.platform] ?? run.platform}
                      </span>
                      <span className="text-[9px] text-red-400 ml-auto">{timeAgo(run.startedAt)}</span>
                    </div>
                    <p className="text-[10px] text-red-600 dark:text-red-400 truncate">{run.errorMessage}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Top Stat Card ──────────────────────────────

function TopStatCard({
  icon: Icon,
  label,
  value,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  value: number;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 text-left hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-sm transition-all group"
    >
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{label}</span>
        <ChevronRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{value.toLocaleString()}</div>
    </button>
  );
}
